import { useParams } from "react-router-dom";
import { useState, useEffect, useCallback } from 'react';
import './ProcurementLinks.css';
import ProcurementTrackerService from '../Services/ProcurementTrackerService';

const getVendorLinkData = async (sessionId, setSuccess, setFailure) => {
    try {
        const response = await ProcurementTrackerService.getProjectVendorLinkData(sessionId);
        if(response.status === 200) {
            setSuccess(response.data);
        } else {
            setFailure(`Error: Received status code ${response.status}`);
        };
    } catch (error) {
        setFailure(`Error: ${error.message}`);
    }
};

const updateProjectLineItem = async (sessionId, lineItemData, setSuccess, setFailure) => {
    try {
        const response = await ProcurementTrackerService.submitVendorLinkEdits(sessionId, lineItemData);
        if (response.status > 199 && response.status < 300) {
            setSuccess(response.data);
        } else {
            console.error('Error updating line item:', response);
            setFailure(`Error: Received status code ${response.status}`);
        };
    } catch (error) {
        console.error('Error updating line item:', error);
        setFailure(`Error: ${error.message}`);
    };
};

const LineItemEditModal = ({ selectedLineItem, closeModal, refreshData, sessionId }) => {
    const [editedSteps, setEditedSteps] = useState([]);
    const [originalShippingAddress, setOriginalShippingAddress] = useState(null);
    const [originalShippingContact, setOriginalShippingContact] = useState(null);
    
    // Form states
    const [description, setDescription] = useState('');
    const [poNumber, setPoNumber] = useState('');
    const [amount, setAmount] = useState('');
    const [externalOrderNumber, setExternalOrderNumber] = useState('');
    const [externalReleaseNumber, setExternalReleaseNumber] = useState('');
    const [externalOffsiteStorage, setExternalOffsiteStorage] = useState('');
    const [notes, setNotes] = useState('');
    const [externalNotes, setExternalNotes] = useState('');
    const [externalProjectManager, setExternalProjectManager] = useState('');
    const [externalProjectEngineer, setExternalProjectEngineer] = useState('');
    const [requestedDate, setRequestedDate] = useState('');
    const [startDate, setStartDate] = useState('');
    const [address, setAddress] = useState('');
    const [address2, setAddress2] = useState('');
    const [city, setCity] = useState('');
    const [state, setState] = useState('');
    const [zip, setZip] = useState('');
    const [country, setCountry] = useState('USA');
    const [firstName, setFirstName] = useState('');
    const [lastName, setLastName] = useState('');
    const [contactNumber, setContactNumber] = useState('');
    const [extension, setExtension] = useState('');
    const [contactEmail, setContactEmail] = useState('');
    const [contactTitle, setContactTitle] = useState('');
    const [schedule, setSchedule] = useState('');
    const [issues, setIssues] = useState([]);
    const [newIssueText, setNewIssueText] = useState('');
    const [editingIssueId, setEditingIssueId] = useState(null);
    const [editingIssueText, setEditingIssueText] = useState('');
    const [showHistoricIssues, setShowHistoricIssues] = useState(false);
    
    useEffect(() => {
        if (selectedLineItem) {
            // Initialize form fields with selectedLineItem data
            setDescription(selectedLineItem.description || '');
            setPoNumber(selectedLineItem.poNumber || '');
            setAmount(selectedLineItem.amount || '');
            setExternalOrderNumber(selectedLineItem.externalOrderNumber || '');
            setExternalReleaseNumber(selectedLineItem.externalReleaseNumber || '');
            setExternalOffsiteStorage(
                selectedLineItem.externalOffsiteStorage === true ? 'Yes' : 
                selectedLineItem.externalOffsiteStorage === false ? 'No' : ''
            );
            setNotes(selectedLineItem.notes || '');
            setExternalNotes(selectedLineItem.externalNotes || '');
            setExternalProjectManager(selectedLineItem.externalProjectManager || '');
            setExternalProjectEngineer(selectedLineItem.externalProjectEngineer || '');
            setRequestedDate(selectedLineItem.requestDate ? new Date(selectedLineItem.requestDate).toISOString().split('T')[0] : '');
            setStartDate(selectedLineItem.startDate ? new Date(selectedLineItem.startDate).toISOString().split('T')[0] : '');
            setSchedule(selectedLineItem.schedule || '');
            setIssues(selectedLineItem.issues || []);
            setNewIssueText('');
            setEditingIssueId(null);
            setEditingIssueText('');
            setShowHistoricIssues(false);
            
            // Store original shipping data
            setOriginalShippingAddress(selectedLineItem.shippingAddress);
            setOriginalShippingContact(selectedLineItem.shippingContact);
            
            // Initialize shipping address
            if (selectedLineItem.shippingAddress) {
                setAddress(selectedLineItem.shippingAddress.address || '');
                setAddress2(selectedLineItem.shippingAddress.address2 || '');
                setCity(selectedLineItem.shippingAddress.city || '');
                setState(selectedLineItem.shippingAddress.state || '');
                setZip(selectedLineItem.shippingAddress.zip || '');
                setCountry(selectedLineItem.shippingAddress.country || 'USA');
            } else {
                setAddress('');
                setAddress2('');
                setCity('');
                setState('');
                setZip('');
                setCountry('USA');
            }
            
            // Initialize shipping contact
            if (selectedLineItem.shippingContact) {
                setFirstName(selectedLineItem.shippingContact.firstName || '');
                setLastName(selectedLineItem.shippingContact.lastName || '');
                setContactNumber(selectedLineItem.shippingContact.phone || '');
                setExtension(selectedLineItem.shippingContact.extension || '');
                setContactEmail(selectedLineItem.shippingContact.email || '');
                setContactTitle(selectedLineItem.shippingContact.title || '');
            } else {
                setFirstName('');
                setLastName('');
                setContactNumber('');
                setExtension('');
                setContactEmail('');
                setContactTitle('');
            }
            
            // Initialize workflow steps
            if (selectedLineItem.steps && selectedLineItem.steps.length > 0) {
                const stepsWithStatus = selectedLineItem.steps.map(stepArray => ({
                    ...stepArray[0],
                    status: stepArray[0].status || 'Not Started'
                }));
                setEditedSteps(stepsWithStatus);
            }
        }
    }, [selectedLineItem]);
    
    // Issue management functions
    const handleAddIssue = () => {
        if (!newIssueText.trim()) return;
        const newIssue = {
            issue: newIssueText,
            active: true
        };
        setIssues([...issues, newIssue]);
        setNewIssueText('');
    };

    const handleEditIssue = (issueId) => {
        const issueToEdit = issues.find(i => i.id === issueId);
        if (issueToEdit) {
            setEditingIssueId(issueId);
            setEditingIssueText(issueToEdit.issue);
        }
    };

    const handleSaveEditIssue = () => {
        if (!editingIssueText.trim()) return;
        setIssues(issues.map(i => 
            i.id === editingIssueId 
                ? { ...i, issue: editingIssueText, edited: true }
                : i
        ));
        setEditingIssueId(null);
        setEditingIssueText('');
    };

    const handleCancelEditIssue = () => {
        setEditingIssueId(null);
        setEditingIssueText('');
    };

    const handleCloseIssue = (issueId) => {
        setIssues(issues.map(i => 
            i.id === issueId 
                ? { ...i, closed: true, active: false }
                : i
        ));
    };
    
    const handleSave = useCallback(() => {
        // Check if shipping address was changed
        const shippingAddressChanged = originalShippingAddress ? (
            originalShippingAddress.address !== address ||
            originalShippingAddress.address2 !== address2 ||
            originalShippingAddress.city !== city ||
            originalShippingAddress.state !== state ||
            originalShippingAddress.zip !== zip ||
            originalShippingAddress.country !== country
        ) : (address || city || state || zip);
        
        // Build shipping address object
        const shippingAddressObj = (address || city || state || zip) ? {
            ...(originalShippingAddress?.id && { id: originalShippingAddress.id }),
            address,
            address2,
            city,
            state,
            zip,
            country,
            ...(shippingAddressChanged && { edited: true })
        } : null;
        
        // Check if shipping contact was changed
        const shippingContactChanged = originalShippingContact ? (
            originalShippingContact.firstName !== firstName ||
            originalShippingContact.lastName !== lastName ||
            originalShippingContact.phone !== contactNumber ||
            originalShippingContact.extension !== extension ||
            originalShippingContact.email !== contactEmail ||
            originalShippingContact.title !== contactTitle
        ) : (firstName || lastName || contactNumber || contactEmail);
        
        // Build shipping contact object
        const shippingContactObj = (firstName || lastName || contactNumber || contactEmail) ? {
            ...(originalShippingContact?.id && { id: originalShippingContact.id }),
            firstName: firstName,
            lastName: lastName,
            phone: contactNumber,
            extension,
            email: contactEmail,
            title: contactTitle,
            ...(shippingContactChanged && { edited: true })
        } : null;
        
        const updatedLineItem = {
            id: selectedLineItem.id,
            projectId: selectedLineItem.projectId,
            projectNumber: selectedLineItem.projectNumber,
            submittalId: selectedLineItem.submittalId,
            submittalName: selectedLineItem.submittalName,
            specId: selectedLineItem.specId,
            specName: selectedLineItem.specName,
            description,
            poNumber,
            vendorId: selectedLineItem.vendorId,
            vendorName: selectedLineItem.vendorName,
            amount: amount ? parseFloat(amount) : null,
            externalOrderNumber,
            externalReleaseNumber,
            externalOffsiteStorage: externalOffsiteStorage === 'Yes' ? true : externalOffsiteStorage === 'No' ? false : null,
            notes,
            externalNotes,
            externalProjectManager,
            externalProjectEngineer,
            requestDate: requestedDate || null,
            startDate: startDate || null,
            schedule: schedule || null,
            issues: issues || [],
            shippingAddress: shippingAddressObj,
            shippingContact: shippingContactObj,
            steps: editedSteps.length > 0 ? editedSteps : undefined,
            updatedBy: 'Vendor Portal'
        };
        
        updateProjectLineItem(sessionId, updatedLineItem, () => {
            refreshData();
            closeModal();
        }, (error) => {
            console.error('Error updating line item:', error);
        });
    }, [sessionId, selectedLineItem, description, poNumber, amount, externalOrderNumber, externalReleaseNumber, externalOffsiteStorage, notes, externalNotes, externalProjectManager, externalProjectEngineer, requestedDate, startDate, schedule, issues, address, address2, city, state, zip, country, firstName, lastName, contactNumber, extension, contactEmail, contactTitle, editedSteps, originalShippingAddress, originalShippingContact, refreshData, closeModal]);
    
    if (!selectedLineItem) return null;
    
    return (
        <div className="pvl-vendor-modal-overlay" onClick={closeModal}>
            <div className="pvl-vendor-modal-container" onClick={(e) => e.stopPropagation()}>
                <div className="pvl-vendor-modal-header">
                    <h2>{selectedLineItem.projectNumber} - {selectedLineItem.submittalName}</h2>
                    <button className="pvl-close-button" onClick={closeModal}>×</button>
                </div>
                <div className="pvl-vendor-modal-content">
                    <div className="pvl-line-item-details-container">
                        {/* Row 1: Submittal Info */}
                        <div className="pvl-line-item-detail-row">
                            <div className="pvl-line-item-detail-group">
                                <div className="pvl-line-item-detail-label">Submittal Name</div>
                                <div className="pvl-line-item-detail-value">{selectedLineItem.submittalName || '-'}</div>
                            </div>
                            <div className="pvl-line-item-detail-group">
                                <div className="pvl-line-item-detail-label">Spec Name</div>
                                <div className="pvl-line-item-detail-value">{selectedLineItem.specName || '-'}</div>
                            </div>
                            <div className="pvl-line-item-detail-group">
                                <div className="pvl-line-item-detail-label">Description</div>
                                <div className="pvl-line-item-detail-value">{selectedLineItem.description || '-'}</div>
                            </div>
                        </div>

                        {/* Row 2: Vendor & Order Info */}
                        <div className="pvl-line-item-detail-row">
                            <div className="pvl-line-item-detail-group">
                                <div className="pvl-line-item-detail-label">PO Number</div>
                                <div className="pvl-line-item-detail-value">{selectedLineItem.poNumber || '-'}</div>
                            </div>
                            <div className="pvl-line-item-detail-group">
                                <div className="pvl-line-item-detail-label">Amount</div>
                                <div className="pvl-line-item-detail-value">{selectedLineItem.amount ? `$${parseFloat(selectedLineItem.amount).toFixed(2)}` : '-'}</div>
                            </div>
                            <div className="pvl-line-item-detail-group">
                                <div className="pvl-line-item-detail-label">External Order #</div>
                                <input
                                    type="text"
                                    className="pvl-line-item-edit-input"
                                    value={externalOrderNumber}
                                    onChange={(e) => setExternalOrderNumber(e.target.value)}
                                />
                            </div>
                            <div className="pvl-line-item-detail-group">
                                <div className="pvl-line-item-detail-label">External Release #</div>
                                <input
                                    type="text"
                                    className="pvl-line-item-edit-input"
                                    value={externalReleaseNumber}
                                    onChange={(e) => setExternalReleaseNumber(e.target.value)}
                                />
                            </div>
                            <div className="pvl-line-item-detail-group">
                                <div className="pvl-line-item-detail-label">Offsite Storage</div>
                                <select
                                    className="pvl-line-item-edit-input"
                                    value={externalOffsiteStorage || ''}
                                    onChange={(e) => setExternalOffsiteStorage(e.target.value)}
                                >
                                    <option value="">-</option>
                                    <option value="Yes">Yes</option>
                                    <option value="No">No</option>
                                </select>
                            </div>
                        </div>

                        {/* Row 3: Date Information */}
                        <div className="pvl-line-item-detail-row">
                            <div className="pvl-line-item-detail-group">
                                <div className="pvl-line-item-detail-label">Start Date</div>
                                <div className="pvl-line-item-detail-value">{selectedLineItem.startDate ? new Date(selectedLineItem.startDate).toLocaleDateString() : '-'}</div>
                            </div>
                            <div className="pvl-line-item-detail-group">
                                <div className="pvl-line-item-detail-label">Requested Completion Date</div>
                                <div className="pvl-line-item-detail-value">{selectedLineItem.requestDate ? new Date(selectedLineItem.requestDate).toLocaleDateString() : '-'}</div>
                            </div>
                            <div className="pvl-line-item-detail-group">
                                <div className="pvl-line-item-detail-label">Expected Completion Date</div>
                                <div className="pvl-line-item-detail-value">{selectedLineItem.expectedCompletionDate ? new Date(selectedLineItem.expectedCompletionDate).toLocaleDateString() : '-'}</div>
                            </div>
                        </div>

                        {/* Row 4: Workflow & Steps */}
                        <div className="pvl-line-item-detail-row">
                            <div className="pvl-pvl-line-item-detail-group pvl-line-item-workflow-section">
                                <div className="pvl-line-item-detail-label">Workflow: {selectedLineItem.workflow || 'Not Set'}</div>
                                {selectedLineItem.steps && selectedLineItem.steps.length > 0 && (
                                    <div className="pvl-line-item-workflow-steps">
                                        {editedSteps.map((step, idx) => {
                                            if (!step) return null;
                                            const isInternal = step.role && typeof step.role === 'object' && step.role.type === 'Internal';
                                            return (
                                                <div key={idx} className="pvl-line-item-workflow-step">
                                                    <div className="pvl-line-item-workflow-step-number">{idx + 1}</div>
                                                    <div className="pvl-line-item-workflow-step-content">
                                                        <div className="pvl-line-item-workflow-step-name">{step.name}</div>
                                                        <div className="pvl-line-item-workflow-step-role">
                                                            {step.role ? (typeof step.role === 'object' ? step.role.name : step.role) : '-'}
                                                        </div>
                                                        <div className="pvl-line-item-workflow-step-leadtime">
                                                            {step.expectedLeadtime ? `${step.expectedLeadtime}d` : '-'}
                                                        </div>
                                                        <div className={`pvl-line-item-workflow-step-expected-date ${step.status === 'Completed' ? 'completed' : 'not-completed'}`}>
                                                            {step.status === 'Completed' 
                                                                ? (step.date ? new Date(step.date).toLocaleDateString() : '-')
                                                                : (step.expectedCompletionDate ? new Date(step.expectedCompletionDate).toLocaleDateString() : '-')
                                                            }
                                                        </div>
                                                        
                                                        {isInternal ? (
                                                            <div className="pvl-line-item-workflow-step-status-readonly">
                                                                {step.status || 'Not Started'}
                                                            </div>
                                                        ) : (
                                                            <>
                                                                <select
                                                                    className="pvl-line-item-workflow-step-status-select"
                                                                    value={step.status || 'Not Started'}
                                                                    onChange={(e) => {
                                                                        const newSteps = [...editedSteps];
                                                                        newSteps[idx] = { ...newSteps[idx], status: e.target.value, editted: true };
                                                                        setEditedSteps(newSteps);
                                                                    }}
                                                                    onClick={(e) => e.stopPropagation()}
                                                                >
                                                                    <option value="Not Started">Not Started</option>
                                                                    <option value="In Progress">In Progress</option>
                                                                    <option value="On Hold">On Hold</option>
                                                                    <option value="Completed">Completed</option>
                                                                </select>
                                                                <div className="pvl-line-item-workflow-step-confirm-date">
                                                                    <label className="pvl-confirm-date-label">Confirm Completion Date:</label>
                                                                    <input
                                                                        type="date"
                                                                        className="pvl-line-item-workflow-step-date-input"
                                                                        value={step.confirmedDate ? new Date(step.confirmedDate).toISOString().split('T')[0] : ''}
                                                                        onChange={(e) => {
                                                                            const newSteps = [...editedSteps];
                                                                            const newDate = e.target.value;
                                                                            if (newDate) {
                                                                                newSteps[idx] = { 
                                                                                    ...newSteps[idx], 
                                                                                    confirmedDate: newDate,
                                                                                    confirmed: true,
                                                                                    date: newDate,
                                                                                    editted: true 
                                                                                };
                                                                            } else {
                                                                                newSteps[idx] = { 
                                                                                    ...newSteps[idx], 
                                                                                    confirmedDate: null,
                                                                                    confirmed: null,
                                                                                    date: null,
                                                                                    editted: true 
                                                                                };
                                                                            }
                                                                            setEditedSteps(newSteps);
                                                                        }}
                                                                        onClick={(e) => e.stopPropagation()}
                                                                    />
                                                                </div>
                                                            </>
                                                        )}
                                                    </div>
                                                    {idx < editedSteps.length - 1 && (
                                                        <div className="pvl-line-item-workflow-step-arrow">→</div>
                                                    )}
                                                </div>
                                            );
                                        })}
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* Row 5: External Notes */}
                        <div className="pvl-line-item-detail-row">
                            <div className="pvl-pvl-line-item-detail-group pvl-line-item-detail-full">
                                <div className="pvl-line-item-detail-label">External Notes</div>
                                <textarea
                                    className="pvl-line-item-edit-textarea"
                                    value={externalNotes}
                                    onChange={(e) => setExternalNotes(e.target.value)}
                                    rows={3}
                                />
                            </div>
                        </div>

                        {/* Row 6: Team */}
                        <div className="pvl-line-item-detail-row">
                            <div className="pvl-line-item-detail-group">
                                <div className="pvl-line-item-detail-label">Project Manager</div>
                                <div className="pvl-line-item-detail-value">{selectedLineItem.projectManager || '-'}</div>
                            </div>
                            <div className="pvl-line-item-detail-group">
                                <div className="pvl-line-item-detail-label">Project Engineer</div>
                                <div className="pvl-line-item-detail-value">{selectedLineItem.projectEngineer || '-'}</div>
                            </div>
                            <div className="pvl-line-item-detail-group">
                                <div className="pvl-line-item-detail-label">External PM</div>
                                <input
                                    type="text"
                                    className="pvl-line-item-edit-input"
                                    value={externalProjectManager}
                                    onChange={(e) => setExternalProjectManager(e.target.value)}
                                />
                            </div>
                            <div className="pvl-line-item-detail-group">
                                <div className="pvl-line-item-detail-label">External PE</div>
                                <input
                                    type="text"
                                    className="pvl-line-item-edit-input"
                                    value={externalProjectEngineer}
                                    onChange={(e) => setExternalProjectEngineer(e.target.value)}
                                />
                            </div>
                        </div>

                        {/* Row 7: Shipping Address */}
                        <div className="pvl-line-item-detail-row">
                            <div className="pvl-line-item-detail-group">
                                <div className="pvl-line-item-detail-label">Shipping Address</div>
                                <div className="pvl-line-item-detail-value">
                                    {selectedLineItem.shippingAddress ? (
                                        <>
                                            {selectedLineItem.shippingAddress.address || ''}
                                            {selectedLineItem.shippingAddress.address2 && `, ${selectedLineItem.shippingAddress.address2}`}
                                            {(selectedLineItem.shippingAddress.city || selectedLineItem.shippingAddress.state || selectedLineItem.shippingAddress.zip) && <br />}
                                            {selectedLineItem.shippingAddress.city && `${selectedLineItem.shippingAddress.city}`}
                                            {selectedLineItem.shippingAddress.state && `, ${selectedLineItem.shippingAddress.state}`}
                                            {selectedLineItem.shippingAddress.zip && ` ${selectedLineItem.shippingAddress.zip}`}
                                            {selectedLineItem.shippingAddress.country && selectedLineItem.shippingAddress.country !== 'USA' && <><br />{selectedLineItem.shippingAddress.country}</>}
                                        </>
                                    ) : '-'}
                                </div>
                            </div>
                        </div>

                        {/* Row 8: Shipping Contact */}
                        <div className="pvl-line-item-detail-row">
                            <div className="pvl-line-item-detail-group">
                                <div className="pvl-line-item-detail-label">Shipping Contact</div>
                                <div className="pvl-line-item-detail-value">
                                    {selectedLineItem.shippingContact ? (
                                        <>
                                            {(selectedLineItem.shippingContact.firstName || selectedLineItem.shippingContact.lastName) && (
                                                <div>
                                                    <strong>{selectedLineItem.shippingContact.firstName} {selectedLineItem.shippingContact.lastName}</strong>
                                                    {selectedLineItem.shippingContact.title && ` - ${selectedLineItem.shippingContact.title}`}
                                                </div>
                                            )}
                                            {selectedLineItem.shippingContact.phone && (
                                                <div>
                                                    Phone: {selectedLineItem.shippingContact.phone}
                                                    {selectedLineItem.shippingContact.extension && ` ext. ${selectedLineItem.shippingContact.extension}`}
                                                </div>
                                            )}
                                            {selectedLineItem.shippingContact.email && (
                                                <div>Email: {selectedLineItem.shippingContact.email}</div>
                                            )}
                                        </>
                                    ) : '-'}
                                </div>
                            </div>
                        </div>

                        {/* Row 9: Schedule */}
                        <div className="pvl-line-item-detail-row">
                            <div className="pvl-line-item-detail-group">
                                <div className="pvl-line-item-detail-label">Schedule UID</div>
                                <input 
                                    type="text" 
                                    className="pvl-line-item-edit-input" 
                                    value={schedule} 
                                    style={{ width: "100px" }}
                                    onChange={(e) => setSchedule(e.target.value)} 
                                />
                            </div>
                        </div>

                        {/* Row 10: Issues */}
                        <div className="pvl-line-item-issues-section">
                            <div className="pvl-line-item-detail-row">
                                <div className="pvl-pvl-line-item-detail-group pvl-line-item-detail-full">
                                    <div className="pvl-line-item-detail-label">Issues</div>
                                    
                                    <div className="pvl-line-item-add-issue">
                                        <textarea
                                            className="pvl-line-item-issue-input"
                                            value={newIssueText}
                                            onChange={(e) => setNewIssueText(e.target.value)}
                                            placeholder="Describe the issue..."
                                            rows={2}
                                        />
                                        <button
                                            className="pvl-add-issue-btn"
                                            onClick={handleAddIssue}
                                            disabled={!newIssueText.trim()}
                                        >
                                            + Add Issue
                                        </button>
                                    </div>
                                    
                                    <div className="pvl-line-item-issues-list">
                                        {issues.filter(issue => issue.active).length > 0 && (
                                            <div className="pvl-issues-list-header">
                                                <span className="pvl-issues-list-title">Active Issues</span>
                                            </div>
                                        )}
                                        {issues
                                            .filter(issue => issue.active)
                                            .map((issue, idx) => (
                                                <div key={issue.id || idx} className="pvl-line-item-issue-item">
                                                    <div className="pvl-issue-item-header">
                                                        <span className="pvl-issue-status-badge pvl-active">Active</span>
                                                        {issue.createdDate && (
                                                            <span className="pvl-issue-date">
                                                                Created: {new Date(issue.createdDate).toLocaleDateString()} by {issue.createdBy || 'Unknown'}
                                                            </span>
                                                        )}
                                                    </div>
                                                    
                                                    {editingIssueId === issue.id ? (
                                                        <div className="pvl-issue-edit-container">
                                                            <textarea
                                                                className="pvl-line-item-issue-input"
                                                                value={editingIssueText}
                                                                onChange={(e) => setEditingIssueText(e.target.value)}
                                                                rows={3}
                                                            />
                                                            <div className="pvl-issue-edit-actions">
                                                                <button
                                                                    className="pvl-save-issue-btn"
                                                                    onClick={handleSaveEditIssue}
                                                                    disabled={!editingIssueText.trim()}
                                                                >
                                                                    Save
                                                                </button>
                                                                <button
                                                                    className="pvl-cancel-issue-btn"
                                                                    onClick={handleCancelEditIssue}
                                                                >
                                                                    Cancel
                                                                </button>
                                                            </div>
                                                        </div>
                                                    ) : (
                                                        <div className="pvl-issue-content">
                                                            <p>{issue.issue}</p>
                                                            <div className="pvl-issue-actions">
                                                                <button
                                                                    className="pvl-edit-issue-btn"
                                                                    onClick={() => handleEditIssue(issue.id)}
                                                                >
                                                                    Edit
                                                                </button>
                                                                <button
                                                                    className="pvl-close-issue-btn"
                                                                    onClick={() => handleCloseIssue(issue.id)}
                                                                >
                                                                    Close Issue
                                                                </button>
                                                            </div>
                                                        </div>
                                                    )}
                                                </div>
                                            ))}
                                    </div>
                                    
                                    {issues.filter(issue => !issue.active).length > 0 && (
                                        <div className="pvl-line-item-closed-issues-section">
                                            <div className="pvl-issues-list-header">
                                                <span className="pvl-issues-list-title">Closed Issues</span>
                                                <button
                                                    className="pvl-toggle-historic-issues-btn"
                                                    onClick={() => setShowHistoricIssues(!showHistoricIssues)}
                                                >
                                                    {showHistoricIssues ? 'Hide' : 'Show'}
                                                </button>
                                            </div>
                                            {showHistoricIssues && (
                                                <div className="pvl-line-item-issues-list">
                                                    {issues
                                                        .filter(issue => !issue.active)
                                                        .map((issue, idx) => (
                                                            <div key={issue.id || idx} className="pvl-line-item-issue-item pvl-closed">
                                                                <div className="pvl-issue-item-header">
                                                                    <span className="pvl-issue-status-badge pvl-closed">Closed</span>
                                                                    {issue.createdDate && (
                                                                        <span className="pvl-issue-date">
                                                                            Created: {new Date(issue.createdDate).toLocaleDateString()} by {issue.createdBy || 'Unknown'}
                                                                        </span>
                                                                    )}
                                                                    {issue.closedDate && (
                                                                        <span className="pvl-issue-date">
                                                                            Closed: {new Date(issue.closedDate).toLocaleDateString()} by {issue.closedBy || 'Unknown'}
                                                                        </span>
                                                                    )}
                                                                </div>
                                                                <div className="pvl-issue-content">
                                                                    <p>{issue.issue}</p>
                                                                </div>
                                                            </div>
                                                        ))}
                                                </div>
                                            )}
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>

                        {/* Action Buttons */}
                        <div className="pvl-line-item-actions-expanded">
                            <button 
                                className="pvl-line-item-save-btn"
                                onClick={handleSave}
                                title="Save"
                            >
                                Save Changes
                            </button>
                            <button 
                                className="pvl-line-item-cancel-btn"
                                onClick={closeModal}
                                title="Cancel"
                            >
                                Cancel
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

const ProcurementLinks = () => {
    const { sessionId } = useParams();
    const [linkData, setLinkData] = useState(null);
    const [error, setError] = useState(null);
    const [selectedLineItem, setSelectedLineItem] = useState(null);
    const [collapsedProjects, setCollapsedProjects] = useState({});
    const [filterType, setFilterType] = useState('Waiting on Vendor'); // 'Waiting on Vendor', 'Waiting on Cornerstone', 'Show All'
    const [statusFilter, setStatusFilter] = useState('All'); // 'All', 'Not Started', 'In Progress', 'On Hold', 'Completed'

    const refreshData = () => {
        getVendorLinkData(sessionId, setLinkData, setError);
    };

    useEffect(() => {
        console.log(linkData);
    }, [linkData]);

    useEffect(() => {
        getVendorLinkData(sessionId, setLinkData, setError);
    }, [sessionId]);

    const toggleProjectCollapse = (projectNumber) => {
        setCollapsedProjects(prev => ({
            ...prev,
            [projectNumber]: !prev[projectNumber]
        }));
    };

    // Filter line items based on filterType and statusFilter
    const filteredLinkData = linkData ? linkData.filter(lineItem => {
        // Filter by waiting on
        let passesWaitingFilter = true;
        if (filterType === 'Waiting on Vendor') {
            passesWaitingFilter = lineItem.nextStep?.role?.type === 'External';
        } else if (filterType === 'Waiting on Cornerstone') {
            passesWaitingFilter = lineItem.nextStep?.role?.type === 'Internal';
        }
        
        // Filter by status
        let passesStatusFilter = true;
        if (statusFilter !== 'All') {
            passesStatusFilter = lineItem.status === statusFilter;
        }
        
        return passesWaitingFilter && passesStatusFilter;
    }) : [];

    // Group line items by project
    const groupedByProject = filteredLinkData.length > 0 ? filteredLinkData.reduce((acc, lineItem) => {
        const projectNumber = lineItem.projectNumber;
        if (!acc[projectNumber]) {
            acc[projectNumber] = [];
        }
        acc[projectNumber].push(lineItem);
        return acc;
    }, {}) : {};

    if (error) {
        return (
            <div className="pvl-procurement-links-container">
                <div className="pvl-procurement-links-error">
                    <h2>Error Loading Data</h2>
                    <p>{error}</p>
                </div>
            </div>
        );
    }

    if (!linkData) {
        return (
            <div className="pvl-procurement-links-container">
                <div className="pvl-procurement-links-loading">
                    <h2>Loading...</h2>
                </div>
            </div>
        );
    }

    return (
        <div className="pvl-procurement-links-container">
            <div className="pvl-procurement-links-header">
                <h1>Procurement Portal</h1>
                <p className="pvl-vendor-name">{linkData[0]?.vendorName || 'Unknown'}</p>
            </div>
            
            <div className="pvl-filter-bar">
                <div className="pvl-filter-group">
                    <div className="pvl-filter-label">Waiting On</div>
                    <div className="pvl-waiting-filter-toggle">
                        <button 
                            className={`pvl-waiting-filter-btn ${filterType === 'Waiting on Vendor' ? 'pvl-active' : ''}`}
                            onClick={() => setFilterType('Waiting on Vendor')}
                        >
                            Vendor
                        </button>
                        <button 
                            className={`pvl-waiting-filter-btn ${filterType === 'Waiting on Cornerstone' ? 'pvl-active' : ''}`}
                            onClick={() => setFilterType('Waiting on Cornerstone')}
                        >
                            Cornerstone
                        </button>
                        <button 
                            className={`pvl-waiting-filter-btn ${filterType === 'Show All' ? 'pvl-active' : ''}`}
                            onClick={() => setFilterType('Show All')}
                        >
                            Show All
                        </button>
                    </div>
                </div>
                <div className="pvl-filter-group">
                    <div className="pvl-filter-label">Status</div>
                    <div className="pvl-status-filter-container">
                        <select 
                            className="pvl-status-filter-select"
                            value={statusFilter}
                            onChange={(e) => setStatusFilter(e.target.value)}
                        >
                            <option value="All">All Status</option>
                            <option value="Not Started">Not Started</option>
                            <option value="In Progress">In Progress</option>
                            <option value="On Hold">On Hold</option>
                            <option value="Completed">Completed</option>
                        </select>
                    </div>
                </div>
            </div>
            
            <div className="pvl-projects-list">
                {Object.keys(groupedByProject).sort().map(projectNumber => {
                    const lineItems = groupedByProject[projectNumber];
                    return (
                        <div key={projectNumber} className="pvl-project-section">
                            <div 
                                className="pvl-project-header" 
                                onClick={() => toggleProjectCollapse(projectNumber)}
                            >
                                <span className="pvl-project-collapse-icon">
                                    {collapsedProjects[projectNumber] ? '▶' : '▼'}
                                </span>
                                <span className="pvl-project-title">
                                    {projectNumber} ({lineItems.length} item{lineItems.length !== 1 ? 's' : ''})
                                </span>
                            </div>
                            
                            {!collapsedProjects[projectNumber] && (
                                <div className="pvl-line-items-table">
                                    <div className="pvl-line-items-table-header">
                                        <div className="pvl-table-cell pvl-table-cell-status">Status</div>
                                        <div className="pvl-table-cell">Submittal Name</div>
                                        <div className="pvl-table-cell">Spec Name</div>
                                        <div className="pvl-table-cell">PO Number</div>
                                        <div className="pvl-table-cell">External Order #</div>
                                        <div className="pvl-table-cell">External Release #</div>
                                        <div className="pvl-table-cell">Ball in Court</div>
                                    </div>
                                    <div className="pvl-line-items-table-body">
                                        {lineItems.map((lineItem, idx) => {
                                            const ballInCourtType = lineItem.ballInCourt?.type === 'Internal' ? 'INT' : lineItem.ballInCourt?.type === 'External' ? 'EXT' : '-';
                                            const ballInCourtRole = lineItem.ballInCourt?.roleName || '-';
                                            const ballInCourtDisplay = ballInCourtType !== '-' && ballInCourtRole !== '-' ? `${ballInCourtType} - ${ballInCourtRole}` : '-';
                                            
                                            return (
                                                <div 
                                                    key={lineItem.id || idx} 
                                                    className="pvl-line-item-wrapper"
                                                    onClick={() => setSelectedLineItem(lineItem)}
                                                >
                                                    <div className="pvl-line-item-row pvl-line-item-row-top">
                                                        <div className="pvl-table-cell pvl-table-cell-status">
                                                            <span className={`pvl-status-badge ${lineItem.status.toLowerCase().replace(' ', '-')}`}>
                                                                {lineItem.status}
                                                            </span>
                                                        </div>
                                                        <div className="pvl-table-cell" title={lineItem.submittalName || '-'}>
                                                            {lineItem.submittalName || '-'}
                                                        </div>
                                                        <div className="pvl-table-cell" title={lineItem.specName || '-'}>
                                                            {lineItem.specName || '-'}
                                                        </div>
                                                        <div className="pvl-table-cell" title={lineItem.poNumber || '-'}>
                                                            {lineItem.poNumber || '-'}
                                                        </div>
                                                        <div className="pvl-table-cell" title={lineItem.externalOrderNumber || '-'}>
                                                            {lineItem.externalOrderNumber || '-'}
                                                        </div>
                                                        <div className="pvl-table-cell" title={lineItem.externalReleaseNumber || '-'}>
                                                            {lineItem.externalReleaseNumber || '-'}
                                                        </div>
                                                        <div className="pvl-table-cell" title={ballInCourtDisplay}>
                                                            {ballInCourtDisplay}
                                                        </div>
                                                    </div>
                                                </div>
                                            );
                                        })}
                                    </div>
                                </div>
                            )}
                        </div>
                    );
                })}
            </div>

            {selectedLineItem && (
                <LineItemEditModal
                    sessionId={sessionId}
                    selectedLineItem={selectedLineItem}
                    closeModal={() => setSelectedLineItem(null)}
                    refreshData={refreshData}
                />
            )}
        </div>
    );
};

export default ProcurementLinks;


